/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Stack,
  TextField,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMerchantAwareNavigate } from '../../hooks/useMerchantAwareNavigate';

import { Button } from '../../components/core';
import { DialogCloseButton } from '../../components/core/DialogCloseButton';
import { MerchantAccessMultiSelect } from '../PortalUsers/components/MerchantAccessMultiSelect';
import { useCreateOnboardingMutation } from '../../store/modules/merchant/api';
import type { MerchantAccess } from '../../store/modules/merchant/types';
import { toast } from '../../util/toast';
import { configurationPaths } from '../../util/constants';
import { normalizeShopifyExternalId } from '../../util/shopifyExternalId';
import { buttonPaddingSx, getApiErrorMessage } from './utils';

const schema = z.object({
  merchants: z.array(z.custom<MerchantAccess>()).min(1, 'Select a merchant'),
  shopDomain: z
    .string()
    .trim()
    .min(1, 'Shop domain is required')
    .refine(
      (value) => !!normalizeShopifyExternalId(value),
      'Enter a valid *.myshopify.com shop domain',
    ),
});

type FormValues = z.infer<typeof schema>;

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const isConflict = (error: unknown) =>
  Boolean(error && typeof error === 'object' && 'status' in error && error.status === 409);

export const AddShopifyOnboardingDialog = ({ isOpen, onClose }: Props) => {
  const navigate = useMerchantAwareNavigate();
  const [createOnboarding, { isLoading }] = useCreateOnboardingMutation();
  const {
    control,
    handleSubmit,
    register,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { merchants: [], shopDomain: '' },
  });

  const close = () => {
    reset();
    onClose();
  };

  const submit = async ({ merchants, shopDomain }: FormValues) => {
    const merchant = merchants[0];
    const normalizedShopDomain = normalizeShopifyExternalId(shopDomain);
    if (!merchant || !normalizedShopDomain) {
      return;
    }

    try {
      await createOnboarding({
        merchantId: merchant.merchantId,
        body: { systemOfOrigin: 'shopify', externalPlatformId: normalizedShopDomain },
      }).unwrap();
      toast.success('Shopify onboarding added to the queue.');
      close();
      navigate(`${configurationPaths.ADMIN_SHOPIFY_PROVISIONING}/${merchant.merchantId}`);
    } catch (error) {
      if (isConflict(error)) {
        close();
        navigate(`${configurationPaths.ADMIN_SHOPIFY_PROVISIONING}/${merchant.merchantId}`);
        return;
      }
      toast.error(getApiErrorMessage(error, 'Unable to add Shopify onboarding.'));
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={close}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 4 } }}
    >
      <DialogCloseButton onClose={close} />
      <DialogTitle sx={{ fontWeight: 600 }}>Add Shopify onboarding</DialogTitle>
      <DialogContent>
        <Stack component="form" spacing={2.5} sx={{ pt: 1 }} onSubmit={handleSubmit(submit)}>
          <Controller
            name="merchants"
            control={control}
            render={({ field }) => (
              <>
                <MerchantAccessMultiSelect
                  value={field.value}
                  onChange={(merchants) => field.onChange(merchants.slice(-1))}
                  label="Existing merchant"
                />
                {errors.merchants && (
                  <FormHelperText error>{errors.merchants.message}</FormHelperText>
                )}
              </>
            )}
          />
          <TextField
            {...register('shopDomain')}
            label="Shopify shop domain"
            placeholder="store.myshopify.com"
            required
            error={!!errors.shopDomain}
            helperText={
              errors.shopDomain?.message ?? 'Use the store hostname, not a storefront URL.'
            }
          />
          <Stack direction="row" justifyContent="flex-end" spacing={1.5} alignItems="center">
            <Button
              text="Cancel"
              theme="secondary"
              variant="outlined"
              sx={buttonPaddingSx}
              onClick={close}
              disabled={isLoading}
            />
            <Button text="Add to queue" type="submit" loading={isLoading} sx={buttonPaddingSx} />
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
